import type { SavedMoment } from '../../hooks/useSavedMoment'
import { useShopLogo } from '../../hooks/useShopLogo'
import type { Place } from '../../types/display'
import ReviewCard from './ReviewCard'
import ShopLogo from './ShopLogo'
import StageFooter from './StageFooter'
import { StagePay, StageSaved, StageThanks, StageWelcome } from './StageStates'

/** What the order side is showing, so the stage can follow it. */
/** `scan`: the order side holds a QR of its own (the receipt), so the
 *  review QR waits rather than stand beside it. */
export type StageMoment = { kind: 'pay'; amount: number } | { kind: 'thanks'; scan?: boolean } | null

/**
 * The shop's canvas, the larger side of the screen: its logo as big as it
 * goes, on white so it looks the way the shop drew it, and later the place
 * for its own slides and ads. One slim strip along the bottom follows the
 * order (the saving, the amount, thanks) and carries our
 * signature, small.
 */
export default function Stage({
  placeId,
  place,
  ordering = false,
  currency = 'DKK',
  moment = null,
  saved = null,
  cachedLogo = true,
}: {
  placeId: string
  place: Place | null
  /** Paint the logo saved from last time while the place loads. Off for
   *  sessions whose branding needs a confirmed grant. */
  cachedLogo?: boolean
  /** A cart is being rung up. */
  ordering?: boolean
  currency?: string
  moment?: StageMoment
  saved?: SavedMoment | null
}) {
  const { src } = useShopLogo(placeId, place, cachedLogo)
  // The review ask belongs to the calm moments: before an order and after it,
  // never next to another QR the customer should scan.
  const ask = !!place?.google_place_id && (moment?.kind === 'thanks' ? !moment.scan : !ordering && !saved && !moment)
  // On a handheld the canvas folds to its strip while there is an order or a
  // payment to show, so the QR and the amount get the screen. basis-0, not
  // h-auto: portrait:h-[44%] comes later in the CSS, and in a column the
  // basis wins over the height, leaving the box at its content.
  const busy = ordering || !!saved || !!moment

  return (
    <div className={`relative flex h-full w-[58%] shrink-0 flex-col bg-white portrait:h-[44%] portrait:w-full ${busy ? 'handheld:basis-0' : ''}`}>
      <div className={`flex min-h-0 flex-1 items-center space-x-cfd-4 px-cfd-gutter py-cfd-3 ${busy ? 'handheld:hidden' : ''}`}>
        <div className="flex h-full min-w-0 flex-1 items-center justify-center">
          <ShopLogo src={src} title={place?.title} />
        </div>
        {ask && place?.google_place_id && <ReviewCard googlePlaceId={place.google_place_id} />}
      </div>

      <div className="flex items-center space-x-cfd-4 border-t border-divider px-cfd-gutter py-cfd-3">
        <div className="min-w-0 flex-1">
          {moment?.kind === 'pay' ? (
            <StagePay amount={moment.amount} currency={currency} />
          ) : moment?.kind === 'thanks' ? (
            <StageThanks />
          ) : saved ? (
            <StageSaved amount={saved.amount} seq={saved.seq} currency={currency} />
          ) : (
            <StageWelcome />
          )}
        </div>
        <StageFooter />
      </div>
    </div>
  )
}
